<script>
import HelperService from '@/services/HelperService';
import PostService from '@/services/PostService';

export default {
  name: 'DigitalPostSearchView',
  data() {
    const initialFilter = {
      visitorEmail: '',
      prisonerNumber: '',
      postType: 'Digital',
      status: '',
      skip: 0,
      limit: 10,
      sort: '-updated_at',
      after: '',
      before: '',
    };

    return {
      loading: true,
      helperService: new HelperService(),
      filters: initialFilter,
      defaultFilters: { ...initialFilter },
      headers: [
        { label: `${this.$options.filters.term('prisoner')} Number`, key: 'prisoner_number' },
        { label: `${this.$options.filters.term('prisoner')}'s Assigned Name`, key: 'prisonerName' },
        { label: 'External Contact', key: 'visitorName' },
        { label: 'Status', key: 'status' },
        { label: 'Updated', key: 'updated_at' },
        { label: 'Staff Assigned', key: 'clinician_assigned' },
        { label: 'Reviewed By', key: 'reviewed_by' },
        { label: '', key: 'actions' },
      ],
      approvalStatuses: [
        { text: 'All', value: '' },
        { text: 'Needs Approval', value: 'AwaitingConfirmation' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      currentPage: 1,
      totalRows: 10,
      post: [],
      debouncedSearch: null,
    };
  },
  watch: {
    currentPage: {
      handler(newPage) {
        localStorage.setItem('digitalPostSearchFilters', JSON.stringify({ ...this.filters, currentPage: newPage }));
        this.getPost();
      },
      initial: false,
    },
    filters: {
      handler(newFilters) {
        localStorage.setItem('digitalPostSearchFilters', JSON.stringify({ currentPage: this.currentPage, ...newFilters }));
      },
      deep: true,
    },
  },
  mounted() {
    const savedFilters = localStorage.getItem('digitalPostSearchFilters');
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      this.filters = { ...this.filters, ...parsedFilters };
      const savedPage = Number(parsedFilters.currentPage) || 1;
      if (savedPage > 1) {
        this.totalRows = savedPage * 10;
        this.$nextTick(() => {
          this.currentPage = savedPage;
        });
      }
    }

    if (this.$route.query.status || this.$route.query.status === '') {
      this.filters.status = this.$route.query.status;
    }

    this.debouncedSearch = this.helperService.debounce(this.searchPost, 300);

    this.getPost();
  },
  methods: {
    async getPost() {
      try {
        const filters = { ...this.filters };
        filters.skip = (this.currentPage - 1) * filters.limit;

        if (filters.before) {
          filters.before = this.helperService.setEndOfDay(filters.before);
        }
        const { data } = await PostService.getAll(filters);

        this.post = data.data;
        this.totalRows = data.count;
      } catch (e) {
        console.error('Error in getPost: ', e);
        this.$toast.error('Could not get post, please refresh and try again');
      } finally {
        this.loading = false;
      }
    },
    async searchPost() {
      this.currentPage = 1;
      await this.getPost();
    },
    viewPostDetails(item) {
      this.$router.push({ name: 'post-view', params: { id: item._id } })
    },
    resetFilters() {
      this.filters = { ...this.defaultFilters };
      this.searchPost();
    },
  },
};
</script>

<template>
  <div>
    <!-- Filters -->
    <section>
      <b-row class="align-items-end">
        <!-- Prisoner Number -->
        <b-col cols="12" md="3" sm="6">
          <label>{{ 'prisoner' | term(true) }} Number</label>
          <b-form-input
            v-model="filters.prisonerNumber"
            :placeholder="'Enter ' + $options.filters.term('prisoner') + ' number'"
            class="mb-1"
            @input="debouncedSearch"
          />
        </b-col>
        <!-- External Contact Email -->
        <b-col cols="12" md="3" sm="6">
          <label>External Contact Email <small>( full email needed )</small></label>
          <b-form-input
            v-model="filters.visitorEmail"
            class="mb-1"
            placeholder="Enter external contact email"
            @input="debouncedSearch"
          />
        </b-col>
        <!-- Status -->
        <b-col cols="12" md="3" sm="6">
          <label>Status</label>
          <b-form-select
            v-model="filters.status"
            class="mb-1"
            @input="searchPost"
          >
            <b-form-select-option
              v-for="(status, i) in approvalStatuses"
              :key="i"
              :value="status.value"
            >
              {{ status.text }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
        <!-- Sort -->
        <b-col cols="12" md="3" sm="6">
          <label>Sort</label>
          <b-form-select
            v-model="filters.sort"
            class="mb-1"
            @input="searchPost"
          >
            <b-form-select-option value="updated_at">Updated (Oldest First)</b-form-select-option>
            <b-form-select-option value="-updated_at">Updated (Newest First)</b-form-select-option>
            <b-form-select-option value="prisoner_number">{{ $options.filters.term('prisoner') | capEachWord }} Number (A-Z)</b-form-select-option>
            <b-form-select-option value="-prisoner_number">{{ $options.filters.term('prisoner') | capEachWord }} Number (Z-A)</b-form-select-option>
            <b-form-select-option value="visitor.firstname">External Contact Name (A-Z)</b-form-select-option>
            <b-form-select-option value="-visitor.firstname">External Contact Name (Z-A)</b-form-select-option>
          </b-form-select>
        </b-col>
      </b-row>
      <b-row class="align-items-end">
        <!-- From Date -->
        <b-col cols="12" md="3" sm="6">
          <label>From Date</label>
          <b-form-datepicker
            v-model="filters.after"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            class="mb-1"
            close-button
            locale="en-uk"
            reset-button
            today-button
            @input="searchPost"
          />
        </b-col>
        <!-- End Date -->
        <b-col cols="12" md="3" sm="6">
          <label>End Date</label>
          <b-form-datepicker
            v-model="filters.before"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            class="mb-1"
            close-button
            locale="en-uk"
            reset-button
            today-button
            @input="searchPost"
          />
        </b-col>
        <!-- Clear Filters Button -->
        <b-col cols="12" md="3" sm="6">
          <b-btn
            class="mb-1 full-width"
            @click="resetFilters"
          >
            Clear Filters
          </b-btn>
        </b-col>
      </b-row>
    </section>

    <!-- Table -->
    <section>
      <b-card no-body>
        <b-table
          :current-page="currentPage"
          :fields="headers"
          :items="post"
          hover
          responsive
          @row-clicked="viewPostDetails"
        >
          <template #cell(visitorName)="data">
            {{ data.item.visitor.firstname }} {{ data.item.visitor.lastname }}
          </template>
          <template #cell(prisonerName)="data">
            {{ data.item.contact.firstname }} {{ data.item.contact.lastname }}
          </template>
          <template #cell(status)="data">
            {{ data.item.status === 'AwaitingConfirmation' ? 'Needs Approval' : data.item.status }}
          </template>
          <template #cell(updated_at)="data">
            {{ data.item.updated_at | formatDateTime }}
          </template>
          <template #cell(clinician_assigned)="data">
            {{ data.item.clinician_assigned ? data.item.clinician_assigned.name : '-' }}
          </template>
          <template #cell(reviewed_by)="data">
            {{ helperService.getReviewedBy(data.item) }}
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :per-page="filters.limit"
          :total-rows="totalRows"
          class="mx-1"
        />
      </b-card>
    </section>
  </div>
</template>
